import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { Signatory } from '@interfaces/report-templates.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowMessage } from '@utils/notification';
import { useMutationSignatory } from '@apis/bff-api/report-templates';

interface ManageSignatoryDialogProps {
    onClose: () => void;
    signatory: Signatory | null;
    onSubmit: (success: boolean, id: string) => void;
    type?: 'add' | 'edit';
    module: 1 | 2 | 4 | 8 | 16 | undefined; // i'm not sure this is the best way how to do this. the magic numbers are coming from the rest schema
}

const COMPONENT_NAME = 'manage-signatory-dialog';

const ManageSignatoryDialog = ({ onClose, signatory, onSubmit, type, module }: ManageSignatoryDialogProps) => {
    const { t } = useTranslation();
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges, discardChanges } =
        useUnsavedChangesContext();
    const studyId = sessionStorage.getItem('studyId');
    const { trigger: putSignatory } = useMutationSignatory();

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<Signatory>({
        mode: 'all',

        defaultValues: {
            id: signatory?.id,
            jobTitle: signatory?.jobTitle || '',
            firstName: signatory?.firstName || '',
            lastName: signatory?.lastName || '',
            module,
            studyId: signatory?.studyId ?? studyId,
        },
    });

    const createdBy = `${stringDateWithoutTimeFromUtc(signatory?.createdDate, 'DD-MMM-YYYY')} by ${signatory?.createdByName}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(signatory?.updatedDate, 'DD-MMM-YYYY')} by ${signatory?.updatedByName}`;

    const onSubmitHandler = async (data: Signatory) => {
        let success: boolean = false;
        let id: string | undefined;

        try {
            const result = await putSignatory(data);

            if (result) {
                success = true;
                if (Array.isArray(result) && result.length > 0) {
                    id = result[0] ?? undefined;
                }

                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
            } else {
                success = false;
                ShowError(t('common:errorMessage'));
            }
        } catch {
            success = false;
            ShowError(t('common:errorMessage'));
        } finally {
            onSubmit(success, id || '');
        }
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, []);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges('manage-signatory-dialog');
            if (!confirmed) {
                return;
            }
            discardChanges();
        }
        onClose();
    };

    return (
        <DraggableDialog
            title={
                type === 'add'
                    ? t('reports:addSignatory')
                    : t('reports:editSignatoryModal', {
                          id: `RP-${signatory?.recordId}`,
                      })
            }
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            dataTestId="manage-signatory-dialog"
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmitHandler)()}
            primaryBtnLabel={type === 'edit' ? t('common:update') : t('common:add')}
            initialHeight={type === 'edit' ? 275 : 225}
            initialWidth={927}
            createdUpdatedInfo={type === 'edit' && <CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
            createdUpdatedBackgroundColour="gray"
        >
            <form id="add-edit-signatory-form" className="k-w-full">
                <div className="k-shrink-0 k-d-flex k-justify-content-center">
                    <TextBoxController
                        control={control}
                        name="jobTitle"
                        data-testid="job-title-input"
                        label={`${t('reports:jobTitle')} *`}
                        maxLength={150}
                        required
                    />
                </div>

                <div className="k-d-flex k-justify-content-start k-gap-4">
                    <div className="!k-basis-1/2">
                        <TextBoxController
                            control={control}
                            name="firstName"
                            data-testid="first-name-input"
                            label={`${t('reports:first')} *`}
                            maxLength={70}
                            required
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <TextBoxController
                            control={control}
                            name="lastName"
                            data-testid="last-name-input"
                            label={`${t('reports:last')} *`}
                            maxLength={70}
                            required
                        />
                    </div>
                </div>
            </form>
        </DraggableDialog>
    );
};

export default ManageSignatoryDialog;
